import {
  ArrowLeftIcon,
  Col,
  Container,
  HeadingText,
  IconButton,
  Row,
  TitleText,
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import React, { useEffect, useState } from 'react'
import { GroupItem } from '../../../../typings'
import ButtonFullWidth from '../../components/ButtonFullWidth'
import SheetWrapper from '../../components/SheetWrapper'
import serverData from '../../utils/server-data'
import {
  COVER_IMAGE_ALTERNATE_DEFAULT_IMAGE_URLS,
  COVER_IMAGE_DEFAULT_IMAGE_URL,
  COVER_IMAGE_MAX_TOTAL_SIZE,
  ERROR_DISPLAY_TYPES,
  MEDIA_FILES_CATEGORIES,
} from '../../constants'

import { useLocalizedContent } from '../../hooks/useLocalizedContent'
import { useUpdatePhotoMutation } from '../../services/updatePhotoApi'
import { useUploadPhotoMutation } from '../../services/uploadPhotoApi'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { showGlobalToast } from '../../store/slices/globalToastSlice'
import {
  clearHandledError,
  setCustomError,
  setHandledError,
} from '../../store/slices/handledErrorSlice'
import { toggleUpdateCoverImage } from '../../store/slices/sheetsSlice'
import ImageRow from './components/ImageRow'
import ImageUploadField from './components/ImageUploadField'
import { isCoverOnCreateFlowEnabled } from '../../utils/features'
import { tracking } from '../../lib/analytics/fpti'
import Events from '../../lib/analytics/eventTags.js'
import * as styles from './coverImage.css'
import {
  selectBackgroundImageUrl,
  setBackgroundImageUrl,
} from '../../store/slices/groupFormSlice'
import {
  ImageMedia,
  updateMediaFilesQuery,
  useGetMediaFilesQuery,
} from '../../services/getMediaFilesApi'
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner'
import { ImageCropper } from './components/ImageCropper'
import { ProposedPhoto } from '../../../typings'
import { removeDuplicatePhotos } from '../../utils/mediaFiles/removeDuplicatePhotos'
import { removePhotoByUrl as filterOutPhoto } from '../../utils/mediaFiles/filterOutPhoto'

interface CoverImageProps {
  groupItem?: GroupItem
  onClickBack?: () => void
}

const CoverImage = (props: CoverImageProps) => {
  const { groupItem, onClickBack } = props
  const groupId = groupItem?.id
  const dispatch = useAppDispatch()
  const { getContent } = useLocalizedContent()
  const [updatePhoto] = useUpdatePhotoMutation()
  const [uploadPhoto] = useUploadPhotoMutation()
  const isUpdateCoverImage = useAppSelector(
    ({ sheets }) => sheets.updateCoverImage,
  )
  const externalSource = serverData.trackingParameters.externalSource
  const [proposedPhoto, setProposedPhoto] = useState<ProposedPhoto>()

  // currently only 1 default photo. There could be more later.
  const defaultPhotos: ImageMedia[] = [
    {
      url: COVER_IMAGE_DEFAULT_IMAGE_URL,
      alternateDefaultUrls: COVER_IMAGE_ALTERNATE_DEFAULT_IMAGE_URLS,
      name: getContent('coverImage.default.photo.description1'),
      isCustom: false,
      isNew: false,
    },
  ]

  const [defaultCoverPhotos, setDefaultCoverPhotos] = useState(defaultPhotos)
  const [chosenPhotoIndex, setChosenPhotoIndex] = useState(
    isCoverOnCreateFlowEnabled() ? 0 : -1,
  )
  const [isSaving, setIsSaving] = useState(false)
  const backgroundImageUrl = useAppSelector(selectBackgroundImageUrl)
  const currentPhoto = {
    url: backgroundImageUrl,
    name: getContent('coverImage.user.custom.photo.description'),
    isCustom: true,
    isNew: false,
    alternateDefaultUrls: [] as Array<string>,
  }
  const { data: mediaFiles = [currentPhoto], isLoading: isFetchingPhotos } =
    useGetMediaFilesQuery(
      {
        category: MEDIA_FILES_CATEGORIES.ALL,
      },
      { skip: !isCoverOnCreateFlowEnabled() },
    )

  const photos: ImageMedia[] = isCoverOnCreateFlowEnabled()
    ? mediaFiles
    : defaultCoverPhotos

  // Remove duplicated photos in case the current selected is in the default photos list.
  useEffect(() => {
    if (!isFetchingPhotos && isCoverOnCreateFlowEnabled()) {
      const photosWithoutDuplicates = removeDuplicatePhotos(mediaFiles)
      const photosWithoutCurrent = filterOutPhoto(
        photosWithoutDuplicates,
        backgroundImageUrl,
      )
      dispatch(updateMediaFilesQuery([currentPhoto, ...photosWithoutCurrent]))
    }
  }, [isFetchingPhotos])

  const addCustomPhotoToList = ({ url, name, isNew = false }: ImageMedia) => {
    const customPhoto: ImageMedia = {
      url,
      name,
      isCustom: true,
      isNew,
    }

    const isDuplicate = photos.find((photo) => photo.url === url)
    const customPhotoExists = !!photos.find((photo) => photo.isCustom)
    if (!isDuplicate) {
      if (customPhotoExists) {
        //replace
        setDefaultCoverPhotos(
          photos.map((photo) => (photo.isCustom ? customPhoto : photo)),
        )
      } else {
        //add to the beginning of the list
        setDefaultCoverPhotos([customPhoto, ...photos])
      }
    }

    setChosenPhotoIndex(0)
  }

  useEffect(() => {
    tracking(
      Events.p2p_pools_edit_photo_screen_shown({
        source: externalSource,
        page_variant: 'creator',
        pool_id: groupId,
      }),
    )
  }, [])

  /**
   * This is a fix due to Native and Web using difference background images as the default.
   * This prevents us from displaying 2 of the same "default" background images:
   * 1. the old web built-in default (/groups/default-background.png)
   * 2. the user selected Native built-in default  (/groups/default-background2x.png)
   * 3. the new Native/Web built-in default (/groups/background/default-background.jpg)
   * @param url
   */
  const updateDefaultImageUrl = (url: string) => {
    const defaultImage = photos.find((photo) =>
      photo.alternateDefaultUrls?.includes(url),
    )
    // when the supplied URL is one of the alternateDefaultUrls, then update the default image URL
    // so that we do not have duplicated images in the list
    if (defaultImage) {
      defaultImage.url = url
    }
  }

  const getBackgroundImageUrl = () => {
    if (isCoverOnCreateFlowEnabled()) {
      return backgroundImageUrl
    }
    return groupItem?.backgroundImageUrl
  }

  useEffect(() => {
    const imageUrl = getBackgroundImageUrl()
    if (imageUrl && !isCoverOnCreateFlowEnabled()) {
      updateDefaultImageUrl(imageUrl)
      const photo: ImageMedia = {
        url: imageUrl,
        name: getContent('coverImage.user.custom.photo.description'),
        isNew: false,
      }
      addCustomPhotoToList(photo)
    }
  }, [groupItem])

  const onError = (error: Error) => {
    dispatch(
      setCustomError({
        title: error.message,
        type: ERROR_DISPLAY_TYPES.IN_PAGE,
      }),
    )
    setIsSaving(false)
  }

  const onComplete = () => {
    setIsSaving(false)
    dispatch(toggleUpdateCoverImage())
    dispatch(clearHandledError())
  }

  const handleClickBack = () => {
    onComplete()
    onClickBack()
  }

  /**
   * Handle when a photo is selected from the list
   * @param photosIndex
   */
  const onSelectCoverImage = (photosIndex: any) => {
    const chosenPhoto = photos[photosIndex]
    if (!chosenPhoto.isCustom) {
      tracking(
        Events.p2p_pools_edit_photo_paypal_provided_option_selected({
          source: externalSource,
          page_variant: 'creator',
          pool_id: groupId,
        }),
      )
    }
    if (!chosenPhoto.isNew) {
      tracking(
        Events.p2p_pools_edit_photo_choose_existing_photo({
          source: externalSource,
          page_variant: 'creator',
          pool_id: groupId,
        }),
      )
    }
    setChosenPhotoIndex(photosIndex)
    dispatch(clearHandledError())
    if (isCoverOnCreateFlowEnabled()) {
      dispatch(setBackgroundImageUrl(chosenPhoto.url))
      handleClickBack()
    }
  }

  const uploadCoverImage = async ({ url, name }: ImageMedia) => {
    setIsSaving(true)
    dispatch(clearHandledError())
    try {
      const uploadResponse = await uploadPhoto({
        file: url,
        filename: name,
      }).unwrap()
      dispatch(setBackgroundImageUrl(uploadResponse.url))
      handleClickBack()
    } catch (error) {
      dispatch(setHandledError(error)(dispatch))
      setIsSaving(false)
    }
  }

  const clickBackButton = () => {
    tracking(
      Events.p2p_pools_edit_photo_back_pressed({
        source: externalSource,
        page_variant: 'creator',
        pool_id: groupId,
      }),
    )
    handleClickBack()
  }

  const saveCoverImage = async () => {
    const chosenPhoto = photos[chosenPhotoIndex]
    let newImageURL = chosenPhoto.url
    setIsSaving(true)
    dispatch(clearHandledError())
    try {
      if (chosenPhoto.isNew) {
        tracking(
          Events.p2p_pools_edit_photo_take_new_photo({
            source: externalSource,
            page_variant: 'creator',
            pool_id: groupId,
          }),
        )
        const uploadResponse = await uploadPhoto({
          file: chosenPhoto.url,
          filename: chosenPhoto.name,
        }).unwrap()
        newImageURL = uploadResponse.url

        /*
          In the scenario where updatePhoto (below) throws an error,
          we will first mutate the supplied photo's URL with the new URL
          So that, when the user re-submits updatePhoto, we do not re-upload the photo to mediaserv
         */
        addCustomPhotoToList({
          url: newImageURL,
          name: chosenPhoto.name,
          isNew: false,
        })
      }

      await updatePhoto({
        id: groupItem.id,
        backgroundImageUrl: newImageURL,
      }).unwrap()
    } catch (error) {
      dispatch(setHandledError(error)(dispatch))
      return setIsSaving(false)
    }

    onComplete()
    return dispatch(
      showGlobalToast({
        isSuccess: true,
        message: getContent('coverImage.success.message'),
      }),
    )
  }

  /**
   * Handle when user adds a photo from their local device
   * @param url
   * @param name
   */
  const handleCustomImageChange = async ({ url, name }: ImageMedia) => {
    tracking(
      Events.p2p_pools_edit_photo_upload_photo_pressed({
        source: externalSource,
        page_variant: 'creator',
        pool_id: groupId,
      }),
    )
    addCustomPhotoToList({
      url,
      name,
      isNew: true,
    })
    setChosenPhotoIndex(0)

    if (isCoverOnCreateFlowEnabled()) {
      await uploadCoverImage({ url, name })
    }
  }

  const FooterContents = () => {
    return (
      <div>
        <ButtonFullWidth
          onClick={saveCoverImage}
          data-testid="upload-photo-cta"
          form="coverImageForm"
          type="button"
        >
          <Message id="coverImage.upload.button" />
        </ButtonFullWidth>
      </div>
    )
  }

  const getSheetContent = () => {
    if (proposedPhoto) {
      return (
        <ImageCropper
          proposedPhoto={proposedPhoto}
          setProposedPhoto={setProposedPhoto}
          onSave={handleCustomImageChange}
          onError={onError}
        />
      )
    }

    return (
      <>
        <Container form>
          <Row>
            <Col form="full">
              <HeadingText size={isCoverOnCreateFlowEnabled() ? 'lg' : 'sm'}>
                <Message id="coverImage.heading" />
              </HeadingText>
              {isCoverOnCreateFlowEnabled() && (
                <TitleText
                  as="p"
                  data-testid="subheading"
                  className={styles.subheading}
                >
                  <Message id="coverImage.subheading" />
                </TitleText>
              )}
            </Col>
          </Row>
          <Row>
            <Col form="full">
              <ImageUploadField
                onError={onError}
                restrictedTotalSize={COVER_IMAGE_MAX_TOTAL_SIZE}
                onChange={handleCustomImageChange}
                data-testid="crowdfunding-cover-image__image-upload"
                setProposedPhoto={setProposedPhoto}
              />
            </Col>
          </Row>
        </Container>
        <Container
          form
          {...(isCoverOnCreateFlowEnabled() && {
            className: styles.scrollableImages,
          })}
        >
          {photos.map((photo, index) => {
            return (
              <Row key={`photo-${index}`}>
                <Col form="full">
                  <ImageRow
                    checked={chosenPhotoIndex === index}
                    photo={photo}
                    index={index}
                    onChange={onSelectCoverImage}
                  />
                </Col>
              </Row>
            )
          })}
          {isCoverOnCreateFlowEnabled() && isFetchingPhotos && (
            <Row>
              <Col form="full">
                <LoadingSpinner usePercentContainerStyle size="lg" />
              </Col>
            </Row>
          )}
        </Container>
      </>
    )
  }

  return (
    <SheetWrapper
      action={
        isCoverOnCreateFlowEnabled() && (
          <IconButton
            data-testid="backButton"
            svgIconComponent={ArrowLeftIcon}
            buttonType="tertiary"
            onClick={
              proposedPhoto
                ? () => setProposedPhoto(undefined)
                : () => clickBackButton()
            }
          />
        )
      }
      title={getContent('coverImage.sheet.accessible.title')}
      hideTitle={true}
      isOpen={isUpdateCoverImage}
      footerContents={!isCoverOnCreateFlowEnabled() && <FooterContents />}
      onCloseComplete={onComplete}
      closeButtonLabel={getContent('coverImage.sheet.close.button')}
      closeButtonProps={{
        'data-testid': 'close-button',
      }}
      onBeforeClose={() => !isSaving}
      isLoading={isSaving}
      center={!isCoverOnCreateFlowEnabled()}
      {...(isCoverOnCreateFlowEnabled() && {
        className: styles.sectionContainer,
      })}
    >
      {isCoverOnCreateFlowEnabled() ? (
        <div className={styles.mainContainer}>{getSheetContent()}</div>
      ) : (
        <form id="coverImageForm" className={styles.mainContainer}>
          {getSheetContent()}
        </form>
      )}
    </SheetWrapper>
  )
}

export default CoverImage
